import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader } from '../context/AuthContext';
import { AdminLayout } from '../components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
  Download,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';
const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ORDER_TYPES = [
  { value: 'dine_in', label: 'Dine In' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'online', label: 'Online' },
];

const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'ready', label: 'Ready' },
  { value: 'served', label: 'Served' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'check', label: 'Check' },
  { value: 'credit', label: 'Credit' },
];

const PAYMENT_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

export default function StaffOrderLogs() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Filters
  const [staffId, setStaffId] = useState('all');
  const [orderType, setOrderType] = useState('all');
  const [orderStatus, setOrderStatus] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [staffList, setStaffList] = useState([]);

  // Fetch staff list for dropdown
  useEffect(() => {
    fetchStaffList();
  }, []);

  // Fetch orders when filters or page changes
  useEffect(() => {
    fetchOrderLogs();
  }, [page, limit, staffId, orderType, orderStatus, paymentMethod, startDate, endDate]);

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/staff`, {
        headers: getAuthHeader(),
      });
      setStaffList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const fetchOrderLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...(staffId && staffId !== 'all' && { staff_id: staffId }),
        ...(orderType && orderType !== 'all' && { order_type: orderType }),
        ...(orderStatus && orderStatus !== 'all' && { order_status: orderStatus }),
        ...(paymentMethod && paymentMethod !== 'all' && { payment_method: paymentMethod }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const response = await axios.get(`${API}/reports/order-logs`, {
        headers: getAuthHeader(),
        params,
      });

      setOrders(response.data.data || []);
      setTotalRecords(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error('Error fetching order logs:', error);
      toast.error('Failed to load order logs');
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setStaffId('all');
    setOrderType('all');
    setOrderStatus('all');
    setPaymentMethod('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleExport = async () => {
    try {
      const params = {
        page: 1,
        limit: 1000,
        ...(staffId && staffId !== 'all' && { staff_id: staffId }),
        ...(orderType && orderType !== 'all' && { order_type: orderType }),
        ...(orderStatus && orderStatus !== 'all' && { order_status: orderStatus }),
        ...(paymentMethod && paymentMethod !== 'all' && { payment_method: paymentMethod }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const response = await axios.get(`${API}/reports/order-logs`, {
        headers: getAuthHeader(),
        params,
      });

      const ordersData = response.data.data || [];
      const csv = convertToCSV(ordersData);
      downloadCSV(csv, 'order-logs');
      toast.success('Orders exported successfully');
    } catch (error) {
      console.error('Error exporting orders:', error);
      toast.error('Failed to export orders');
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    const headers = [
      'Order ID',
      'Table',
      'Type',
      'Status',
      'Items',
      'Total',
      'Payment Status',
      'Payment Method',
      'Created By',
      'Role',
      'Customer',
      'Phone',
      'Duration (mins)',
      'Date/Time',
    ];

    const rows = data.map((order) => [
      order.order_id || 'N/A',
      order.table_name || '-',
      order.order_type || '-',
      order.order_status || '-',
      order.items_count || 0,
      order.grand_total || 0,
      order.payment_status || '-',
      order.payment_method || '-',
      order.created_by_name || 'N/A',
      order.created_by_role || '-',
      order.customer_name || '-',
      order.customer_phone || '-',
      order.completed_in_minutes || '-',
      order.created_at ? new Date(order.created_at).toLocaleString() : '-',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      accepted: 'bg-blue-100 text-blue-700 border-blue-300',
      ready: 'bg-purple-100 text-purple-700 border-purple-300',
      served: 'bg-cyan-100 text-cyan-700 border-cyan-300',
      completed: 'bg-green-100 text-green-700 border-green-300',
      cancelled: 'bg-red-100 text-red-700 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      refunded: 'bg-blue-100 text-blue-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft size={20} />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Order Logs</h1>
                  <p className="text-sm text-gray-600">
                    Total: {totalRecords} orders | Page {page} of {totalPages}
                  </p>
                </div>
              </div>
              {/* <Button onClick={handleExport} variant="outline" size="sm">
              <Download size={18} className="mr-2" />
              Export
            </Button> */}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters Card */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter size={20} />
                Filters
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
              >
                Reset Filters
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Staff Filter */}
              <div className="space-y-2">
                <Label htmlFor="staff">Staff Member</Label>
                <Select value={staffId} onValueChange={setStaffId}>
                  <SelectTrigger id="staff">
                    <SelectValue placeholder="All staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Order Type Filter */}
              <div className="space-y-2">
                <Label htmlFor="order-type">Order Type</Label>
                <Select value={orderType} onValueChange={setOrderType}>
                  <SelectTrigger id="order-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {ORDER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Order Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="order-status">Order Status</Label>
                <Select value={orderStatus} onValueChange={setOrderStatus}>
                  <SelectTrigger id="order-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                    {ORDER_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Method Filter */}
              <div className="space-y-2">
                <Label htmlFor="payment-method">Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger id="payment-method">
                    <SelectValue placeholder="All methods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date Filter */}
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              {/* End Date Filter */}
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              {/* Results Per Page */}
              <div className="space-y-2">
                <Label htmlFor="limit">Results Per Page</Label>
                <Select value={String(limit)} onValueChange={(val) => {
                  setLimit(Number(val));
                  setPage(1);
                }}>
                  <SelectTrigger id="limit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading orders...</p>
                </div>
              </div>
            ) : orders.length === 0 ? (
              <div className="flex items-center justify-center h-96">
                <p className="text-gray-600 text-lg">No orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Table
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Created By
                      </th>
                      {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Duration
                      </th> */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Date & Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {orders.map((order) => (
                      <tr key={order.order_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                          {order.order_id?.substring(0, 8) || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {order.table_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-block capitalize px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                            {order.order_type?.replace('_', ' ') || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`inline-block capitalize px-2 py-1 rounded text-xs font-medium border ${getStatusColor(
                              order.order_status
                            )}`}
                          >
                            {order.order_status || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-center">
                          {order.items_count || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                          {formatCurrency(order.grand_total)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="space-y-1">
                            <div
                              className={`inline-block px-2 py-1 rounded text-xs font-medium ${getPaymentStatusColor(
                                order.payment_status
                              )}`}
                            >
                              {order.payment_status || 'pending'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.payment_method?.toLowerCase() || '-'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="space-y-1">
                            <div className="font-medium text-gray-800">
                              {order.created_by_name}
                            </div>
                            <div className="text-xs text-gray-500 capitalize">
                              {order.created_by_role}
                            </div>
                          </div>
                        </td>
                        {/* <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="space-y-1">
                            <div className="font-medium text-gray-800">
                              {order.customer_name || '-'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.customer_phone || '-'}
                            </div>
                          </div>
                        </td> */}
                        {/* <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {order.completed_in_minutes !== null ? (
                            <div className="flex items-center gap-1 text-gray-700">
                              <Clock size={16} />
                              <span>{order.completed_in_minutes} min</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td> */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {order.created_at
                            ? new Date(order.created_at).toLocaleString('en-IN')
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing page <span className="font-semibold">{page}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
              {' '} ({totalRecords} total orders)
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || loading}
              >
                <ChevronLeft size={16} />
                Previous
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (page <= 3) {
                    pageNum = idx + 1;
                  } else if (page > totalPages - 3) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = page - 2 + idx;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                      disabled={loading}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || loading}
              >
                Next
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
}
